import { useState, useEffect } from 'react';
import { Award, Target, Users, Zap, Shield, Star, TrendingUp, Heart } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Target, Zap, Users, Award, Shield, Star, TrendingUp, Heart
};

export default function AboutUs() {
  const [content, setContent] = useState({
    hero_title: 'About The Circuit',
    hero_subtitle: "Redefining executive workspaces in Bangalore's most strategic locations",
    hero_image: 'https://images.unsplash.com/photo-1666718623430-da207b018ea3?w=1920&q=90',
    story_title: 'Our Story',
    story_paragraphs: [],
    values_title: 'Our Values',
    values_subtitle: 'The principles that guide everything we do at The Circuit.',
    why_choose_title: 'Why Choose The Circuit?'
  });
  const [valueCards, setValueCards] = useState([]);
  const [whyChoosePoints, setWhyChoosePoints] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, valuesRes, whyChooseRes] = await Promise.all([
          axios.get(`${API_URL}/api/about-page-content`),
          axios.get(`${API_URL}/api/value-cards`),
          axios.get(`${API_URL}/api/why-choose-points`)
        ]);
        setContent(contentRes.data);
        setValueCards(valuesRes.data);
        setWhyChoosePoints(whyChooseRes.data);
      } catch (error) {
        console.error('Error fetching about page data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="about-page" className="pt-20">
      <SEO page="about" />
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${content.hero_image})` }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{content.hero_title}</h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto">
            {content.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#222] mb-6">{content.story_title}</h2>
            <div className="space-y-4 text-[#666] text-base leading-relaxed">
              {content.story_paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F5F5F5]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#222] mb-4">{content.values_title}</h2>
            <p className="text-[#666] text-base max-w-2xl mx-auto">
              {content.values_subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valueCards.map((value, index) => {
              const Icon = iconMap[value.icon] || Target;
              return (
                <div key={value.id || index} className="bg-white p-6 rounded-lg text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E12429] rounded-full mb-4">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#222] mb-3">{value.title}</h3>
                  <p className="text-[#666] text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#222] mb-8 text-center">{content.why_choose_title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whyChoosePoints.map((point) => (
                <div key={point.id} className="border-l-4 border-[#E12429] pl-4">
                  <h3 className="text-xl font-semibold text-[#222] mb-2">{point.title}</h3>
                  <p className="text-[#666] text-sm">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}